import DeviceModule from "@/store/modules/device";
import FiskalyModule from "@/store/modules/fiskaly";
import { IDEFiskalyVATDefinition, ITransaction } from "@/types/db/transaction";
import CashFloatModule from "@/store/modules/cash-float";
import { LogLevel, LogService } from "@/services/logging";
import { IProductSlotBooking } from "@/types/db/product-slot-booking";
import {
  getTransactionsByCashfloatUuid,
  saveTransaction,
} from "@/db/transaction";
import { AccountingState, TransactionTypes } from "@/types/db/accounting";
import { BookingStatus } from "@/types/db/product-slot-state";
import { Currencies } from "@/constants/currency";
import {
  closeCashPointMutation,
  completeTransactionMutation,
  startTransactionMutation,
} from "@/api/gql/fiskaly";
import UserModule from "@/store/modules/user";
import POSConfigModule from "@/store/modules/pos-config";
import { PaymentMethodName as PaymentMethodNameGraphQL } from "@/types/gql/generated/graphql-badges";
import { generateUuidv4 } from "./app";
import dayjs from "dayjs";
import {
  ICompleteTransactionArgs,
  ITransactionItemInput,
  ITransactionItemVatInput,
  ITransactionPaymentInput,
  PaymentMethodName,
} from "@smeetz/cert-common/src/lib/interfaces/transaction.types";
import {
  closingCashStatementFromTransactions,
  transformToCashPointClosingTransaction,
} from "@smeetz/cert-common";
import { getTaxValue } from "./tax";
import { IAmountsPerVAT } from "@/interfaces/receipts";
import { saveCashPointClosure } from "@/db/cashpoint-closure";
import { ICashPointClosure } from "@/types/db/cashpoint-closure";
// import {
//   bookingStateToOrderState,
//   convertBookingToOrderSession,
//   getSessionOrderType,
// } from "./order-session";
import {
  completeOrder,
  signAustriaReceiptMutation,
  startOrder,
} from "@/api/gql/badges";
import { isGermanCompliant } from "../vue-helpers/compliance";

const vatDefinitions: IDEFiskalyVATDefinition[] = [
  {
    vat_definition_export_id: 1,
    percentage: 19,
    description: "Regelsteuersatz", // Standard
  },
  {
    vat_definition_export_id: 2,
    percentage: 7,
    description: "Ermäßigter Steuersatz", // Reduced
  },
];

export async function startTransaction(bookingUuid: string): Promise<void> {
  if (
    !DeviceModule.hardwareLocation ||
    !DeviceModule.hardwareLocation.FiskalyCashRegisterId ||
    isGermanCompliant.value === false
  ) {
    return;
  }

  const groupId = POSConfigModule.groupId || UserModule.getUserGroupId;
  const uuid = generateUuidv4();
  const createdAtTimestamp = Date.now();
  const posRegisterId = DeviceModule.hardwareLocation.FiskalyCashRegisterId;

  const lastSeqId = FiskalyModule.lastTransaction?.seqId || 1;
  const lastReceiptNumber = FiskalyModule.lastTransaction?.receiptNumber || 1;

  const transaction: ITransaction = {
    bookingUuid,
    uuid,
    seqId: lastSeqId + 1,
    groupId: POSConfigModule.groupId,
    createdAtTimestamp,
    receiptNumber: lastReceiptNumber + 1,
    cashfloatUuid: String(CashFloatModule.cashFloat?.uid),
    posRegisterId: posRegisterId,
    cashfloatSeqId: CashFloatModule.cashFloat?.seqId || 0,
    completeTransaction: null,
    closingTransaction: null,
    signature: null,
    qrcode: null,
  };

  FiskalyModule.addNewTransaction(transaction);
}

export async function endTransaction(
  booking: IProductSlotBooking
): Promise<void> {
  if (
    !DeviceModule.hardwareLocation ||
    !DeviceModule.hardwareLocation.FiskalyCashRegisterId ||
    isGermanCompliant.value === false
  ) {
    return;
  }

  // get the transaction from the list of transactions in progress
  const transaction =
    FiskalyModule.transactionsInProgress[String(booking.uuid)];
  if (!transaction) {
    // couldn't find the transaction
    LogService.info(
      `Fiskaly: Couldn't find transaction for booking ${booking.uuid}`,
      undefined,
      LogLevel.Error
    );
    return;
  }

  // build completed transaction shape
  const completedTransaction = completeTransactionDetails(booking, transaction);
  transaction.completeTransaction = completedTransaction;

  await singNewTransaction(transaction, completedTransaction);
}

/**
 * Complete transaction details
 * @param booking
 * @param transaction
 */
export function completeTransactionDetails(
  booking: IProductSlotBooking,
  transaction: ITransaction
): ICompleteTransactionArgs {
  // We need all the items and payments related to this transaction (session)
  // get all payments related to this transaction
  const paymentAndDiscountAccountings = booking.accountings.filter(
    (acc) =>
      acc.fiskalyTransactionUuid === transaction.uuid &&
      acc.state === AccountingState.Completed &&
      (acc.transactionType === TransactionTypes.TRANSACTION_TYPE_PAYMENT ||
        acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT ||
        acc.transactionType === TransactionTypes.TRANSACTION_TYPE_REFUND)
  );

  // get all pss uuids affected by this transaction
  const paymentAndDiscountAccountingEntries = paymentAndDiscountAccountings
    .map((acc) => acc.accountingEntriesList)
    .flat();

  const pssUuids = [
    ...new Set(
      paymentAndDiscountAccountingEntries.map((ae) =>
        String(ae.productSlotStateUuid)
      )
    ),
  ];

  // we need TICKET accountingEntries related to these pss
  const ticketAccountingEntries = booking.accountings
    .filter(
      (acc) =>
        acc.transactionType === TransactionTypes.TRANSACTION_TYPE_TICKET &&
        !acc.refund &&
        acc.accountingEntriesList[0].productSlotStateUuid &&
        pssUuids.includes(acc.accountingEntriesList[0].productSlotStateUuid)
    )
    .map((acc) => acc.accountingEntriesList)
    .flat();

  const pssList = booking.pssList.filter(
    (pss) => pss.uuid && pssUuids.includes(pss.uuid)
  );

  // group pss by priceId, isDiscount, isCancelled and voucherId
  const groupedItems: ITransactionItemInput[] = [];
  for (let i = 0; i < pssList.length; i++) {
    const pssDiscountValue = 0; // totalDiscountsByPss.value(String(pssList[i].uuid));
    const isDiscounted = pssDiscountValue > 0 ? 1 : 0;
    const isCancelled =
      pssList[i].bookingStatus === BookingStatus.Canceled ? 1 : 0;

    const ticketAE = ticketAccountingEntries.find(
      (ae) => ae.productSlotStateUuid === pssList[i].uuid
    );
    // get vat of this pss
    let vat: ITransactionItemVatInput | null = null;
    if (
      ticketAE &&
      ticketAE.taxId &&
      ticketAE.salesTaxRate &&
      ticketAE.salesTaxMicroValue
    ) {
      const vatRate = ticketAE?.salesTaxRate / 10000;
      vat = {
        vatId: getVatDefinitionExportIdByRate(vatRate),
        vatRate: vatRate,
        itemTotalVAT: Math.abs(ticketAE.salesTaxMicroValue / 1_000_000),
        vatPaid:
          getTaxValue(pssList[i].priceValue - pssDiscountValue, {
            rateMiliValue: ticketAE.salesTaxRate,
            includedInPrice: ticketAE.salesTaxIncluded,
            id: ticketAE.taxId,
            name: "",
            rate: 0,
          }) / 1000_000,
      };
    }

    const groupedPSS = groupedItems.find(
      (pss) =>
        pss.priceId === pssList[i].priceId &&
        pss.priceValue === pssList[i].priceValue &&
        pss.isCancelled === isCancelled &&
        pss.isDiscount === isDiscounted
    );
    if (!groupedPSS) {
      // This pss is not grouped yet, lets add it with quantity = 1
      const item: ITransactionItemInput = {
        priceId: pssList[i].priceId,
        categoryId: pssList[i].categoryId,
        productId: pssList[i].productId,
        priceValue: pssList[i].priceValue / 100,
        total: (pssList[i].priceValue - pssDiscountValue) / 100,
        quantity: 1,
        isCancelled,
        isDiscount: isDiscounted,
        vats: vat ? [vat] : [],
      };
      // if this pss is a Voucher we need to add the voucherId
      // TODO: AYMEN

      groupedItems.push(item);
    } else {
      // This item already exists, lets increase its quantity by one and update the totals
      groupedPSS.quantity = groupedPSS.quantity + 1;
      groupedPSS.total += (pssList[i].priceValue - pssDiscountValue) / 100;

      // with the new TAX logic, we only have one tax per price
      if (!groupedPSS.vats.length && vat) {
        groupedPSS.vats.push(vat);
      } else if (vat) {
        groupedPSS.vats[0].itemTotalVAT += vat.itemTotalVAT;
        groupedPSS.vats[0].vatPaid += vat.vatPaid;
      }
    }
  }

  //group payments by paymentMethod, transactionType and currency
  const payments: ITransactionPaymentInput[] = [];
  for (const acc of paymentAndDiscountAccountings) {
    let totalPaid = 0;
    let vatAmount = 0;
    for (const ae of acc.accountingEntriesList) {
      totalPaid += ae.value / 100;
      if (acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT) {
        // Discount accountings (Promocode/Discount) have the vat value in their accountingEtries
        vatAmount += (ae.salesTaxMicroValue || 0) / 1_000_000;
      } else {
        // let's get the tax rate from the ticketAE of this pss
        const ticketAE = ticketAccountingEntries.find(
          (tae) => tae.productSlotStateUuid === ae.productSlotStateUuid
        );
        if (ticketAE && ticketAE.salesTaxRate) {
          vatAmount +=
            getTaxValue(ae.value, {
              rateMiliValue: ticketAE.salesTaxRate,
              includedInPrice: ticketAE.salesTaxIncluded,
              id: ticketAE.taxId,
              name: "",
              rate: 0,
            }) / 1_000_000;
        }
      }
    }

    // group payments by type (paymentMethod), transactionType and currency
    const groupedPayment = payments.find(
      (p) =>
        p.type === (acc.paymentMethod as unknown as PaymentMethodName) &&
        p.transactionType === acc.transactionType &&
        // p.currency === acc.currency && // TODO: AYMEN
        ((!p.promocodeId && !acc.promoCodeId) ||
          p.promocodeId === String(acc.promoCodeId))
    );
    if (!groupedPayment) {
      const payment: ITransactionPaymentInput = {
        type: acc.paymentMethod as unknown as PaymentMethodName,
        transactionType: acc.transactionType,
        totalPaid: totalPaid.toFixed(2),
        vatAmount: vatAmount.toFixed(2),
        // currency: acc.currency as Currencies, // TODO: AYMEN
      };
      if (acc.promoCodeId) {
        payment.promocodeId = String(acc.promoCodeId);
      }
      payments.push(payment);
    } else {
      // this item exists, let's update the totalPaid and vatAmount
      groupedPayment.totalPaid = (
        Number(groupedPayment.totalPaid) + totalPaid
      ).toFixed(2);
      groupedPayment.vatAmount = (
        Number(groupedPayment.vatAmount) + vatAmount
      ).toFixed(2);
    }
  }

  const completedTransaction: ICompleteTransactionArgs = {
    seqId: transaction.seqId,
    transactionUUID: transaction.uuid,
    cashfloatSeqId: Number(transaction.cashfloatSeqId),
    bookingUUID: transaction.bookingUuid,
    posRegisterId: String(transaction.posRegisterId),
    groupId: Number(POSConfigModule.groupId),
    receiptNumber: transaction.receiptNumber,
    payments: payments,
    items: groupedItems,
  };

  return completedTransaction;
}

/**
 * Close cashpoint
 * @param cashFloatUuid
 */
export async function closeCashPoint(): Promise<IAmountsPerVAT[] | null> {
  if (
    !DeviceModule.hardwareLocation ||
    !DeviceModule.hardwareLocation.FiskalyCashRegisterId ||
    isGermanCompliant.value === false ||
    !CashFloatModule.cashFloat
  ) {
    return null;
  }

  const cashfloat = CashFloatModule.cashFloat;
  const posRegisterId = DeviceModule.hardwareLocation.FiskalyCashRegisterId;
  const uuid = generateUuidv4();
  const amountsPerVat: IAmountsPerVAT[] = [];
  try {
    // get all Fiskaly transactions related to this cashfloat from indexedDB
    const closingTransactions = (
      await getTransactionsByCashfloatUuid(cashfloat.uid)
    )
      .filter((t) => t.closingTransaction)
      .map((t) => JSON.parse(t.closingTransaction as string));

    // generate the cash statement for the closure receipt
    const closingStatement =
      closingCashStatementFromTransactions(closingTransactions);
    console.log("closingStatement: ", closingStatement);
    if (!closingStatement || !closingStatement.business_cases) return null;
    const amounts =
      closingStatement.business_cases.find((bc) => bc.type === "Umsatz")
        ?.amounts_per_vat_id || [];
    for (const row of amounts) {
      amountsPerVat.push({
        currency: Currencies.EUR,
        vatRate: `${getVatRateByExportId(row.vat_definition_export_id).toFixed(
          2
        )}%`,
        vatAmount: row.vat.toFixed(2),
        net: row.excl_vat.toFixed(2),
        gross: row.incl_vat.toFixed(2),
      });
    }
  } catch (error) {
    LogService.info(
      `Fiskaly: Error when generating the closing statement for cashfloat ${cashfloat.uid}`,
      error,
      LogLevel.Error
    );
  }

  // save the closing to indexedDB so that we can sync it to backend
  const cashPointClosure: ICashPointClosure = {
    cashfloatUid: cashfloat.uid,
    closureUuid: uuid,
    groupId: Number(cashfloat.groupId),
    posRegisterId: posRegisterId,
    seqId: cashfloat.seqId,
    closedAt: dayjs().toISOString(),
  };

  // check if we have any unsynced transactions for this cashfloat
  let closed = false;
  const unsyncedTransactions =
    FiskalyModule.unsyncedTransactions[cashPointClosure.cashfloatUid] || [];
  if (!unsyncedTransactions.length) {
    // we can close the cash point
    try {
      LogService.info(
        `Fiskaly: going to close cashPoint: ${cashPointClosure.closureUuid}`,
        {
          params: cashPointClosure,
        }
      );
      const res = await closeCashPointMutation({
        ...cashPointClosure,
      });
      if (res) {
        closed = true;
      }
    } catch (error) {
      LogService.info(
        `Fiskaly: error when closing cashPointClosure: ${cashPointClosure.closureUuid}, we will try again`,
        {
          params: {
            error: error,
            closure: cashPointClosure,
          },
        },
        LogLevel.Error
      );
    }
  }

  if (!closed) {
    // insert the cash point closure into indexedDB
    try {
      await saveCashPointClosure(cashPointClosure);
    } catch (err: any) {
      LogService.info(
        `Fiskaly: error when saving cashPointClosure: ${cashfloat.uid}`,
        {
          params: {
            error: err,
            cashPointClosure: cashPointClosure,
          },
        }
      );
    }
  }
  return amountsPerVat;
}

/**
 * Sign cash transfer transaction
 */
export async function signCashTransferTransaction(
  cashTransferUid: string,
  amount: number
) {
  if (
    !DeviceModule.hardwareLocation ||
    !DeviceModule.hardwareLocation.FiskalyCashRegisterId ||
    isGermanCompliant.value === false ||
    !CashFloatModule.cashFloat
  ) {
    return null;
  }
  const groupId = POSConfigModule.groupId || UserModule.getUserGroupId;
  const uuid = generateUuidv4();
  const createdAtTimestamp = Date.now();
  const posRegisterId = DeviceModule.hardwareLocation.FiskalyCashRegisterId;
  const lastSeqId = FiskalyModule.lastTransaction?.seqId || 1;
  const lastReceiptNumber = FiskalyModule.lastTransaction?.receiptNumber || 1;

  const transaction: ITransaction = {
    bookingUuid: "",
    uuid,
    seqId: lastSeqId + 1,
    groupId: groupId,
    createdAtTimestamp,
    receiptNumber: lastReceiptNumber + 1,
    cashfloatUuid: String(CashFloatModule.cashFloat?.uid),
    posRegisterId: posRegisterId,
    cashfloatSeqId: CashFloatModule.cashFloat?.seqId || 0,
    completeTransaction: null,
    closingTransaction: null,
    signature: null,
    qrcode: null,
  };
  const completedTransaction: ICompleteTransactionArgs = {
    seqId: transaction.seqId,
    transactionUUID: transaction.uuid,
    cashfloatSeqId: Number(transaction.cashfloatSeqId),
    bookingUUID: transaction.bookingUuid,
    posRegisterId: String(transaction.posRegisterId),
    groupId: Number(POSConfigModule.groupId),
    receiptNumber: transaction.receiptNumber,
    payments: [
      {
        type: PaymentMethodName.CASH,
        totalPaid: String(amount),
        vatAmount: "0",
        transactionType: TransactionTypes.TRANSACTION_TYPE_CASH_TRANSFER,
      },
    ],
    items: [],
  };
  transaction.completeTransaction = completedTransaction;

  LogService.info(
    `Fiskaly: Going to sing transaction for cashTransfer: ${cashTransferUid}`
  );

  await singNewTransaction(transaction, completedTransaction);
}

function getVatDefinitionExportIdByRate(vatRate: number): number {
  const vatDefinition = vatDefinitions.find(
    (vat) => vat.percentage === vatRate
  );
  if (vatDefinition) {
    return vatDefinition.vat_definition_export_id;
  }
  return 0;
}

function getVatRateByExportId(vatExportId: number): number {
  const vatDefinition = vatDefinitions.find(
    (vat) => vat.vat_definition_export_id === vatExportId
  );
  if (vatDefinition) {
    return vatDefinition.percentage;
  }
  return 0;
}

export async function singNewTransaction(
  transaction: ITransaction,
  completedTransaction: ICompleteTransactionArgs
): Promise<void> {
  try {
    LogService.info(
      `Fiskaly: going to sign new transaction: ${transaction.uuid}`,
      {
        params: transaction,
      }
    );
    const res = await startTransactionMutation({
      seqId: transaction.seqId,
      transactionUUID: transaction.uuid,
      cashfloatUid: transaction.cashfloatUuid,
      cashfloatSeqId: Number(transaction.cashfloatSeqId),
      bookingUUID: transaction.bookingUuid,
      posRegisterId: transaction.posRegisterId,
      groupId: Number(transaction.groupId),
    });
    if (res && res.signature) {
      transaction.signature = res.signature;
    }
  } catch (error) {
    LogService.info(
      `Fiskaly: Couldn't sign new transaction: ${transaction.uuid}}`,
      transaction,
      LogLevel.Error
    );
  }

  if (transaction.signature) {
    // transaction was started by backend, let's try to complete it through backend as well
    try {
      LogService.info(
        `Fiskaly: going to sign completed transaction: ${transaction.uuid}`,
        {
          params: transaction,
        }
      );
      const res = await completeTransactionMutation({
        bookingUUID: String(transaction.bookingUuid),
        transactionUUID: transaction.uuid,
        seqId: transaction.seqId,
        cashfloatSeqId: Number(transaction.cashfloatSeqId),
        posRegisterId: String(transaction.posRegisterId),
        groupId: Number(transaction.groupId),
        receiptNumber: transaction.receiptNumber,
        payments: completedTransaction.payments.map((p) => {
          return {
            ...p,
            type: p.type as unknown as PaymentMethodNameGraphQL,
          };
        }),
        items: completedTransaction.items,
      });
      if (res) {
        transaction.qrcode = res.qrCodeData || null;
        transaction.closingTransaction = res.closingTransaction;
      }
    } catch (error) {
      LogService.info(
        `Fiskaly: Couldn't sign completed transaction ${transaction.uuid}`,
        { transaction, error },
        LogLevel.Error
      );
    }
  }

  // if either starting or completing transaction call failed, we generate the closingTransaction locally
  if (!transaction.qrcode || !transaction.closingTransaction) {
    LogService.info(
      `Fiskaly: going to complete transaction locally: ${transaction.uuid}`,
      {
        params: transaction,
      }
    );
    // let's save this transaction in unsynceTransactions array
    FiskalyModule.addUnsyncedTransaction({
      cashfloatUuid: transaction.cashfloatUuid,
      uuid: transaction.uuid,
    });

    // add completedAt manually
    transaction.completedAt = dayjs().toISOString();

    const closingTransaction = transformToCashPointClosingTransaction(
      completedTransaction,
      {
        transactionStartTime: dayjs(
          transaction.createdAtTimestamp
        ).toISOString(),
        transactionEndTime: dayjs().toISOString(),
      }
    );
    transaction.closingTransaction = JSON.stringify(closingTransaction);
  }

  // save transaction to indexedDB
  await saveTransaction(transaction);
}

export async function startAndCompleteItalyOrder(booking: IProductSlotBooking) {
  if (
    !DeviceModule.hardwareLocation ||
    !DeviceModule.hardwareLocation.FiskalyCashRegisterId ||
    POSConfigModule.getGroupCountryCode !== "IT"
  ) {
    return;
  }
  if (!booking.uuid) {
    LogService.info("Can't sign order: No UUID found in booking");
    return;
  }
  // const orderSessionId = BookingModule.currentOrderSessionId;
  // if (!orderSessionId) {
  //   LogService.info("Can't sign order: No current order session found");
  //   return;
  // }
  // const bookingState = booking.state;
  // BookingModule.completeOrderSession({
  //   bookingUuid: booking.uuid,
  //   orderSessionId,
  //   state: bookingStateToOrderState(bookingState),
  // });
  // const orderSession = booking.orderSessions.find(
  //   (os) => os.id === orderSessionId
  // );
  // const deviceUid = DeviceModule.hardwareLocation?.deviceUid;
  // if (!orderSession) {
  //   LogService.info(
  //     `Can't sign order: Order session with id ${orderSessionId} not found in booking`
  //   );
  //   return;
  // }
  // if (!deviceUid) {
  //   LogService.info("Can't sign order: Device UID not found");
  //   return;
  // }

  // const convertedOrderSession = await convertBookingToOrderSession(
  //   booking,
  //   orderSessionId
  // );
  // if (!convertedOrderSession) {
  //   LogService.info(
  //     "Can't sign order: Failed to convert booking to order session"
  //   );
  //   return;
  // }

  // if (
  //   !convertedOrderSession.orderLines.length &&
  //   !convertedOrderSession.payments.length
  // ) {
  //   LogService.info(
  //     "Will not sign order: Order session does not contain order lines nor payments"
  //   );
  //   return;
  // }
  // const startOrderData = {
  //   deviceUid,
  //   orderSession,
  // };
  // try {
  //   await startOrder(startOrderData);
  // } catch (error) {
  //   LogService.info("Can't sign order: Failed to start order", { error });
  //   return;
  // }

  // const orderType = getSessionOrderType(booking, convertedOrderSession);
  // const completeOrderData = {
  //   deviceUid,
  //   orderType: orderType,
  //   orderSession: convertedOrderSession,
  // };
  // let resp;
  // try {
  //   resp = await completeOrder(completeOrderData);
  // } catch (error) {
  //   if (error.message === "Mutation request timed out") {
  //     LogService.info("Can't sign order: Timeout when completing order", {
  //       error,
  //     });
  //     return;
  //   }
  //   LogService.info("Can't sign order: Failed to complete order", { error });
  //   return;
  // }
  // const respData = resp.data?.completeOrder;
  // if (!respData || !respData.govId || !respData.url || !respData.signature) {
  //   LogService.info("Can't sign order: unexpected response", {
  //     response: resp.data,
  //   });
  //   return;
  // }
  // orderSession.signatureInfoIT = {
  //   govId: respData.govId,
  //   url: respData.url,
  //   signature: respData.signature,
  // };
  // orderSession.orderType = orderType;
  // return {
  //   govId: respData.govId,
  //   url: respData.url,
  //   signature: respData.signature,
  // };
}

export async function signAustriaReceipt(booking: IProductSlotBooking) {
  if (
    !DeviceModule.hardwareLocation ||
    !DeviceModule.hardwareLocation.FiskalyCashRegisterId ||
    POSConfigModule.getGroupCountryCode !== "AT"
  ) {
    return;
  }
  if (!booking.uuid) {
    LogService.info("Can't sign order (Austria): No UUID found in booking");
    return;
  }
  // const orderSessionId = BookingModule.currentOrderSessionId;
  // if (!orderSessionId) {
  //   LogService.info(
  //     "Can't sign order (Austria): No current order session found"
  //   );
  //   return;
  // }
  // const bookingState = booking.state;
  // BookingModule.completeOrderSession({
  //   bookingUuid: booking.uuid,
  //   orderSessionId,
  //   state: bookingStateToOrderState(bookingState),
  // });
  // const orderSession = booking.orderSessions.find(
  //   (os) => os.id === orderSessionId
  // );
  // const deviceUid = DeviceModule.hardwareLocation?.deviceUid;
  // if (!orderSession) {
  //   LogService.info(
  //     `Can't sign order (Austria): Order session with id ${orderSessionId} not found in booking`
  //   );
  //   return;
  // }
  // if (!deviceUid) {
  //   LogService.info("Can't sign order (Austria): Device UID not found");
  //   return;
  // }

  // const convertedOrderSession = await convertBookingToOrderSession(
  //   booking,
  //   orderSessionId
  // );
  // if (!convertedOrderSession) {
  //   LogService.info(
  //     "Can't sign order (Austria): Failed to convert booking to order session"
  //   );
  //   return;
  // }

  // if (
  //   !convertedOrderSession.orderLines.length &&
  //   !convertedOrderSession.payments.length
  // ) {
  //   LogService.info(
  //     "Will not sign order (Austria): Order session does not contain order lines nor payments"
  //   );
  //   return;
  // }
  // const signingData = {
  //   deviceUid,
  //   orderSession: convertedOrderSession,
  // };
  // let resp;
  // try {
  //   resp = await signAustriaReceiptMutation(signingData);
  // } catch (error) {
  //   LogService.info("Can't sign order (Austria): request to sign failed", {
  //     error,
  //   });
  //   return;
  // }

  // const respData = resp.data?.signAustriaReceipt;
  // if (
  //   !respData ||
  //   !respData.qrCodeData ||
  //   !respData.cashRegisterSerialNumber ||
  //   !respData.signed ||
  //   !respData.timeSignature ||
  //   !respData.receiptNumber
  // ) {
  //   LogService.info("Can't sign order (Austria): unexpected response", {
  //     response: resp.data,
  //   });
  //   return;
  // }

  // const signatureInfoAT = {
  //   qrCodeData: respData.qrCodeData,
  //   cashRegisterSerialNumber: respData.cashRegisterSerialNumber,
  //   signed: respData.signed,
  //   timeSignature: respData.timeSignature,
  //   receiptNumber: respData.receiptNumber,
  // };
  // orderSession.signatureInfoAT = signatureInfoAT;
  // return signatureInfoAT;
}
